import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";

const LocationPage = lazy(() => import("./pages/LocationPage"));
const WebsiteDesignerPage = lazy(() => import("./pages/WebsiteDesignerPage"));
const BestWebsiteDeveloperPage = lazy(() => import("./pages/BestWebsiteDeveloperPage"));
const MetaAdsExpertPage = lazy(() => import("./pages/MetaAdsExpertPage"));
const BestDigitalMarketingCompanyPage = lazy(() => import("./pages/BestDigitalMarketingCompanyPage"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Process = lazy(() => import("./pages/Process"));
const Resources = lazy(() => import("./pages/Resources"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const LocationsDirectory = lazy(() => import("./pages/LocationsDirectory"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Chatbot = lazy(() => import("./components/Chatbot"));

const queryClient = new QueryClient();

const BackgroundEffects = () => (
  <div className="fixed inset-0 z-[40] pointer-events-none overflow-hidden">
    {/* Subtle Grid */}
    <div 
      className="absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage: 'linear-gradient(to right, hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--primary-foreground)) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
        maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 100%)'
      }}
    />
    
    {/* Noise Texture */}
    <div 
      className="absolute inset-0 opacity-[0.02] mix-blend-overlay"
      style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
      }}
    />

    {/* Glowing Orbs - Hidden on mobile for performance */}
    <div className="hidden md:block absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-primary/10 blur-[120px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '8s' }} />
    <div className="hidden md:block absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-accent/10 blur-[120px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BackgroundEffects />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/process" element={<Process />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/locations/:city" element={<LocationPage />} />
              <Route path="/website-designer/:city" element={<WebsiteDesignerPage />} />
              <Route path="/best-website-developer/:city" element={<BestWebsiteDeveloperPage />} />
              <Route path="/meta-ads-expert/:city" element={<MetaAdsExpertPage />} />
              <Route path="/best-digital-marketing-company/:city" element={<BestDigitalMarketingCompanyPage />} />
              <Route path="/locations-directory" element={<LocationsDirectory />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/industries/:slug" element={<IndustryPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Chatbot />
          </Suspense>
        </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
